'use client'

import { type RefObject, useEffect, useRef } from 'react'

/** How much of the element must be on screen to count as seen. */
export const ANNOUNCEMENT_SEEN_RATIO = 0.5

/**
 * Calls `onSeen` ONCE, the first time `ref`'s element is at least half on
 * screen while the tab is visible (#14619). "Seen" is a claim about a
 * person's eyes, so a banner scrolled away or a popup in a background tab
 * does not count; if the element is on screen while the tab is hidden, the
 * call waits for the tab to come back.
 *
 * `enabled: false` observes nothing — the caller already knows it was seen.
 */
export function useAnnouncementSeen(
  ref: RefObject<Element | null>,
  enabled: boolean,
  onSeen: () => void,
): void {
  const onSeenRef = useRef(onSeen)
  onSeenRef.current = onSeen

  useEffect(() => {
    const element = ref.current
    if (!enabled || !element || typeof IntersectionObserver === 'undefined') {
      return
    }
    let onScreen = false
    let fired = false

    const maybeFire = () => {
      if (fired || !onScreen || document.visibilityState !== 'visible') return
      fired = true
      onSeenRef.current()
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          onScreen = entry.intersectionRatio >= ANNOUNCEMENT_SEEN_RATIO
        }
        maybeFire()
      },
      { threshold: [0, ANNOUNCEMENT_SEEN_RATIO, 1] },
    )
    observer.observe(element)
    document.addEventListener('visibilitychange', maybeFire)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', maybeFire)
    }
  }, [ref, enabled])
}
